import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Share2, Loader2, AlertTriangle, Calendar, Mail, Shield, User, Check, Clock } from 'lucide-react';
import api from '../../services/api';
import { ProtectionBadge } from './ProtectionBadges';

/**
 * Expiration presets
 */
const EXPIRATION_OPTIONS = [
  { value: 'never', label: 'Never expires' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: 'custom', label: 'Custom date' }
];

/**
 * Modal for sharing documents with clients
 */
const ShareDocumentModal = ({ isOpen, onClose, documents = [], caseId, onShare }) => {
  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [expirationOption, setExpirationOption] = useState('30');
  const [customDate, setCustomDate] = useState('');
  const [notify, setNotify] = useState(true);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Filter out protected documents
  const shareableDocs = documents.filter(doc => {
    const protections = doc.protections || [];
    return !protections.some(p => p.prevents_client_sharing);
  });

  const protectedDocs = documents.filter(doc => {
    const protections = doc.protections || [];
    return protections.some(p => p.prevents_client_sharing);
  });

  useEffect(() => {
    if (isOpen && caseId) {
      fetchContacts();
    }
  }, [isOpen, caseId]);

  const fetchContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all case contacts
      const response = await api.get(`/cases/${caseId}/contacts`);
      setContacts(response.data);

      // Auto-select if only one contact with portal access
      const portalContacts = response.data.filter(c => c.portal_enabled);
      if (portalContacts.length === 1) {
        setSelectedContactId(portalContacts[0].id);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load case contacts');
    } finally {
      setLoading(false);
    }
  };

  const getExpirationDate = () => {
    if (expirationOption === 'never') return null;
    if (expirationOption === 'custom') return customDate || null;

    const days = parseInt(expirationOption);
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString();
  };

  const handleShare = async () => {
    if (!selectedContactId) {
      setError('Please select a client to share with');
      return;
    }

    if (shareableDocs.length === 0) {
      setError('No documents available to share');
      return;
    }

    setSharing(true);
    setError(null);

    try {
      const response = await api.post('/documents/share', {
        document_ids: shareableDocs.map(d => d.id),
        contact_id: selectedContactId,
        expires_at: getExpirationDate(),
        notify: notify
      });

      setSuccess(true);

      // Notify parent
      if (onShare) {
        onShare(response.data);
      }

      // Close after brief delay
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Error sharing documents:', err);
      setError(err.response?.data?.detail || 'Failed to share documents');
    } finally {
      setSharing(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-lg mx-4 border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Share2 className="text-blue-400" size={20} />
            <h2 className="text-lg font-semibold text-white">Share with Client</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700 rounded transition-colors"
          >
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {success ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-4">
                <Clock className="text-amber-400" size={32} />
              </div>
              <p className="text-lg font-medium text-white">Share Request Submitted</p>
              <p className="text-sm text-slate-400 mt-1 text-center px-4">
                An attorney will review and approve this request.<br/>
                The client will be notified once approved.
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-blue-500" size={24} />
              <span className="ml-2 text-slate-400">Loading contacts...</span>
            </div>
          ) : (
            <>
              {/* Document list */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Documents to Share ({shareableDocs.length})
                </label>
                <div className="max-h-32 overflow-y-auto bg-slate-900/50 rounded-lg border border-slate-700 p-2 space-y-1">
                  {shareableDocs.map(doc => (
                    <div key={doc.id} className="text-sm text-slate-300 truncate py-1 px-2">
                      {doc.name}
                    </div>
                  ))}
                </div>
              </div>

              {/* Protected documents warning */}
              {protectedDocs.length > 0 && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Shield className="text-amber-400 mt-0.5 flex-shrink-0" size={16} />
                    <div>
                      <p className="text-sm font-medium text-amber-400">
                        {protectedDocs.length} document{protectedDocs.length > 1 ? 's' : ''} excluded
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        The following protected documents cannot be shared:
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {protectedDocs.slice(0, 3).map(doc => (
                          <li key={doc.id} className="text-xs text-slate-400 flex items-center gap-1">
                            <span className="truncate max-w-[200px]">{doc.name}</span>
                            {doc.protections?.map(p => (
                              <ProtectionBadge key={p.category_code} code={p.category_code} showLabel={false} size="sm" />
                            ))}
                          </li>
                        ))}
                        {protectedDocs.length > 3 && (
                          <li className="text-xs text-slate-500">
                            ...and {protectedDocs.length - 3} more
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact selection */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <User size={14} className="inline mr-1" />
                  Share with
                </label>
                {contacts.length === 0 ? (
                  <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700 text-sm text-slate-400">
                    No client contact assigned to this case.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {contacts.map(contact => (
                      <label
                        key={contact.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          !contact.portal_enabled
                            ? 'bg-slate-900/30 border-slate-700 opacity-60 cursor-not-allowed'
                            : selectedContactId === contact.id
                              ? 'bg-blue-500/20 border-blue-500'
                              : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <input
                          type="radio"
                          name="contact"
                          value={contact.id}
                          checked={selectedContactId === contact.id}
                          onChange={(e) => setSelectedContactId(e.target.value)}
                          disabled={!contact.portal_enabled}
                          className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {contact.contact_name}
                          </p>
                          {contact.primary_email_address && (
                            <p className="text-xs text-slate-400 truncate">{contact.primary_email_address}</p>
                          )}
                        </div>
                        {contact.portal_enabled ? (
                          <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 border border-green-500/30 rounded">
                            Portal Active
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs bg-slate-500/20 text-slate-400 border border-slate-500/30 rounded">
                            No Portal Access
                          </span>
                        )}
                      </label>
                    ))}
                    {!contacts.some(c => c.portal_enabled) && (
                      <p className="text-xs text-amber-400 mt-2">
                        <AlertTriangle size={12} className="inline mr-1" />
                        Enable portal access for this client in Contacts to share documents.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Calendar size={14} className="inline mr-1" />
                  Expires
                </label>
                <div className="flex flex-wrap gap-2">
                  {EXPIRATION_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setExpirationOption(option.value)}
                      className={`
                        px-3 py-1.5 rounded-lg text-sm transition-colors
                        ${expirationOption === option.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {expirationOption === 'custom' && (
                  <input
                    type="date"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-2 w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                )}
              </div>

              {/* Attorney approval notice */}
              <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <Clock className="text-amber-400 mt-0.5 flex-shrink-0" size={16} />
                <div className="text-sm">
                  <p className="text-amber-400 font-medium">Attorney Approval Required</p>
                  <p className="text-slate-400 mt-0.5">
                    An attorney must approve this share before the client can download.
                  </p>
                </div>
              </div>

              {/* Notify option */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  className="w-4 h-4 rounded bg-slate-700 border-slate-600 text-blue-600 focus:ring-blue-500"
                />
                <Mail size={14} className="text-slate-400" />
                <span className="text-sm text-slate-300">Notify client via email when approved</span>
              </label>

              {/* Error display */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400">
                  <AlertTriangle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex justify-end gap-2 p-4 border-t border-slate-700">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleShare}
              disabled={sharing || shareableDocs.length === 0 || !selectedContactId}
              className={`
                px-4 py-2 rounded transition-colors flex items-center gap-2
                ${sharing || shareableDocs.length === 0 || !selectedContactId
                  ? 'bg-blue-600/50 text-white/50 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                }
              `}
            >
              {sharing ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Sharing...
                </>
              ) : (
                <>
                  <Share2 size={16} />
                  Share
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ShareDocumentModal;
